// Product comparison stored in localStorage. Cards toggle handles; the compare
// page fetches each product's compare-data view and renders cards plus a table.
(() => {
  const STORAGE_KEY = 'cross-guard-compare';
  const MAX = 4;

  const read = () => {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(value) ? value.filter((h) => typeof h === 'string') : [];
    } catch (error) {
      return [];
    }
  };

  const write = (handles) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(handles.slice(0, MAX)));
    } catch (error) {
      /* storage unavailable: comparison is simply not remembered */
    }
    document.dispatchEvent(new CustomEvent('cross-guard:compare-change', { detail: { handles } }));
  };

  const announce = (text) => {
    let region = document.getElementById('compare-live-region');
    if (!region) {
      region = document.createElement('div');
      region.id = 'compare-live-region';
      region.className = 'visually-hidden';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      document.body.append(region);
    }
    region.textContent = '';
    window.setTimeout(() => (region.textContent = text), 50);
  };

  const syncToggles = () => {
    const handles = read();
    document.querySelectorAll('.product-compare__toggle').forEach((toggle) => {
      toggle.checked = handles.includes(toggle.dataset.compareHandle);
    });
    let bar = document.getElementById('compare-bar');
    if (handles.length === 0) {
      if (bar) bar.remove();
      return;
    }
    if (!bar) {
      bar = document.createElement('div');
      bar.id = 'compare-bar';
      bar.className = 'compare-bar';
      bar.innerHTML = `<span class="compare-bar__text"></span><a class="button button--arrow compare-bar__link" href="${window.crossGuard?.comparePageUrl || '/pages/compare'}"><span>${window.crossGuard?.strings?.compareLabel || 'Compare'}</span></a>`;
      document.body.append(bar);
    }
    const text = bar.querySelector('.compare-bar__text');
    if (text) text.textContent = `${handles.length} / ${MAX}`;
  };

  document.addEventListener('change', (event) => {
    const toggle = event.target.closest('.product-compare__toggle');
    if (!toggle) return;
    const handle = toggle.dataset.compareHandle;
    let handles = read();
    if (toggle.checked) {
      if (handles.length >= MAX && !handles.includes(handle)) {
        toggle.checked = false;
        announce(window.crossGuard?.strings?.compareLimit || `You can compare up to ${MAX} products.`);
        return;
      }
      if (!handles.includes(handle)) handles.push(handle);
      announce(`${toggle.dataset.compareTitle}: ${window.crossGuard?.strings?.compareAdded || 'added to compare'}`);
    } else {
      handles = handles.filter((h) => h !== handle);
      announce(`${toggle.dataset.compareTitle}: ${window.crossGuard?.strings?.compareRemoved || 'removed from compare'}`);
    }
    write(handles);
    syncToggles();
  });

  document.addEventListener('shopify:section:load', syncToggles);
  syncToggles();

  if (!customElements.get('product-compare')) {
    customElements.define(
      'product-compare',
      class ProductCompare extends HTMLElement {
        connectedCallback() {
          this.status = this.querySelector('[data-compare-status]');
          this.cards = this.querySelector('[data-compare-cards]');
          this.table = this.querySelector('[data-compare-table]');
          this.clearButton = this.querySelector('[data-compare-clear]');
          this.strings = this.dataset;
          this.clearButton?.addEventListener('click', () => {
            write([]);
            syncToggles();
            this.render();
          });
          this.render();
        }

        async render() {
          const handles = read();
          this.cards.innerHTML = '';
          this.table.innerHTML = '';
          if (handles.length === 0) {
            this.status.hidden = true;
            this.clearButton.hidden = true;
            this.table.innerHTML = `<div class="cg-empty"><p class="cg-empty__title">${this.strings.stringsEmptyTitle}</p><p class="cg-empty__text">${this.strings.stringsEmptyText}</p><p style="margin-top:1.6rem"><a class="button button--secondary" href="${this.dataset.browseUrl}">${this.dataset.browseLabel}</a></p></div>`;
            return;
          }
          this.status.hidden = false;
          this.status.textContent = this.strings.stringsLoading;
          const products = (
            await Promise.all(
              handles.map((handle) =>
                fetch(`/products/${encodeURIComponent(handle)}?view=compare-data`)
                  .then((r) => (r.ok ? r.json() : null))
                  .catch(() => null)
              )
            )
          ).filter(Boolean);
          this.status.hidden = true;
          this.clearButton.hidden = products.length === 0;
          if (products.length === 0) {
            write([]);
            this.render();
            return;
          }
          products.forEach((p) => this.cards.append(this.card(p)));
          this.table.append(this.specTable(products));
        }

        card(p) {
          const el = document.createElement('article');
          el.className = 'product-card';
          const availability = p.quoteOnly
            ? this.strings.stringsQuoteOnly
            : p.available
            ? this.strings.stringsInStock
            : this.strings.stringsOutOfStock;
          el.innerHTML = `
            <div class="product-card__media">
              ${p.image ? `<img src="${p.image}" alt="${this.escape(p.imageAlt)}" loading="lazy" width="600" height="600">` : ''}
              ${p.demo ? `<div class="product-card__badges"><span class="cg-badge cg-badge--demo">${this.strings.stringsDemo}</span></div>` : ''}
            </div>
            <div class="product-card__body">
              <h2 class="product-card__title"><a href="${p.url}">${this.escape(p.title)}</a></h2>
              ${p.summary ? `<p class="product-card__summary">${this.escape(p.summary)}</p>` : ''}
              <p class="product-card__price">${this.escape(p.price)}</p>
              <span class="cg-availability${p.quoteOnly ? ' cg-availability--quote' : p.available ? '' : ' cg-availability--out'}">${availability}</span>
              <div class="product-card__footer">
                <a class="button button--secondary" href="${p.url}">${this.strings.stringsView}</a>
                <button type="button" class="link compare__remove" data-remove="${this.escape(p.handle)}">${this.strings.stringsRemove}</button>
              </div>
            </div>`;
          el.querySelector('[data-remove]').addEventListener('click', () => {
            write(read().filter((h) => h !== p.handle));
            syncToggles();
            this.render();
          });
          return el;
        }

        specTable(products) {
          const wrapper = document.createElement('div');
          const heading = document.createElement('h2');
          heading.className = 'compare__table-heading';
          heading.textContent = this.strings.stringsSpecHeading;
          const scroller = document.createElement('div');
          scroller.className = 'compare__table-wrapper';
          const rows = [
            ['Price', products.map((p) => p.price)],
            ['Availability', products.map((p) => (p.quoteOnly ? this.strings.stringsQuoteOnly : p.available ? this.strings.stringsInStock : this.strings.stringsOutOfStock))],
          ];
          const specKeys = Object.keys(products[0].specs || {});
          specKeys.forEach((key) => {
            const values = products.map((p) => (p.specs && p.specs[key]) || '');
            if (values.some((v) => v)) rows.push([key, values]);
          });
          const table = document.createElement('table');
          table.className = 'compare__table';
          table.innerHTML = `<thead><tr><th scope="col">${this.strings.stringsAttribute}</th>${products
            .map((p) => `<th scope="col">${this.escape(p.title)}</th>`)
            .join('')}</tr></thead>`;
          const body = document.createElement('tbody');
          rows.forEach(([label, values]) => {
            const tr = document.createElement('tr');
            tr.innerHTML = `<th scope="row">${this.escape(label)}</th>${values
              .map((v) => `<td>${this.escape(v || this.strings.stringsNotSpecified)}</td>`)
              .join('')}`;
            body.append(tr);
          });
          table.append(body);
          scroller.append(table);
          wrapper.append(heading, scroller);
          return wrapper;
        }

        escape(value) {
          return String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
        }
      }
    );
  }
})();
